import { useState } from "react";
import { Link } from "react-router-dom";
import { events } from "@/mocks/events";
import SectionHeading from "@/components/base/SectionHeading";
import StatusBadge from "@/components/base/StatusBadge";
import HeatMeter from "@/components/base/HeatMeter";

const tabs = [
  { key: "heat", name: "热度榜" },
  { key: "latest", name: "最新更新" },
];

export default function RankingSection() {
  const [active, setActive] = useState<"heat" | "latest">("heat");

  const list =
    active === "heat"
      ? [...events].sort((a, b) => b.heat - a.heat)
      : [...events].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));

  return (
    <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
      <div className="w-full">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            kicker="榜单"
            title="追瓜榜 · 值得优先补课的几件事"
            desc="不想全看？先从榜单里挑一个，十分钟把最热的故事线补完。"
          />
          <div className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-100 px-1 py-1 self-start lg:self-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActive(tab.key as "heat" | "latest")}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 cursor-pointer ${
                  active === tab.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <ol className="lg:col-span-8 flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {list.map((event, idx) => (
              <li key={event.id}>
                <Link
                  to={`/event/${event.id}`}
                  className="flex items-center gap-4 p-4 transition-colors duration-200 hover:bg-background-100 cursor-pointer"
                >
                  <span
                    className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-md font-heading text-sm font-bold ${
                      idx < 3
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-200 text-foreground-600"
                    }`}
                  >
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">
                        {event.category}
                      </span>
                      <StatusBadge status={event.status} />
                    </div>
                    <h3 className="mt-1 truncate font-heading text-sm font-bold text-foreground-950 md:text-base">
                      {event.title}
                    </h3>
                    <p className="mt-1 hidden text-xs text-foreground-500 sm:block">
                      更新于 {event.updatedAt} · {event.readingTime}读完
                    </p>
                  </div>
                  <div className="hidden md:block shrink-0">
                    <HeatMeter heat={event.heat} />
                  </div>
                </Link>
              </li>
            ))}
          </ol>

          <div className="lg:col-span-4 flex flex-col gap-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">
                追瓜小提示
              </h3>
              <ul className="mt-4 space-y-3.5">
                <li className="flex gap-3">
                  <span className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 text-sm">
                    <i className="ri-time-line" />
                  </span>
                  <p className="text-xs leading-relaxed text-foreground-600">
                    先看时间线首尾，再回头补中间节点，能最快抓住事件主线。
                  </p>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-sm">
                    <i className="ri-eye-line" />
                  </span>
                  <p className="text-xs leading-relaxed text-foreground-600">
                    带「待求证」标注的内容请谨慎转述，等权威回应更稳妥。
                  </p>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-sm">
                    <i className="ri-refresh-line" />
                  </span>
                  <p className="text-xs leading-relaxed text-foreground-600">
                    事件页会持续追加节点，隔几天回来看看，可能又有新反转。
                  </p>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-500 text-background-50 text-lg">
                <i className="ri-quill-pen-line" />
              </span>
              <h3 className="mt-3 font-heading text-base font-bold text-secondary-900">
                发现我们漏了什么？
              </h3>
              <p className="mt-1.5 text-xs leading-relaxed text-secondary-800">
                把线索和链接发给我们，编辑会核对后补进时间线。
              </p>
              <Link
                to="/about#contact"
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-secondary-600 cursor-pointer"
              >
                去投稿
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}